/**
 * Argus — iconCache LRU pentru iconițele primite inline în WS payload (bloc ws-icons-inline 2026-05-05).
 *
 * Cache memory `Map<iconHash, IconBundle>` cu 3 Blob URL-uri per hash (20/48/96 px).
 * Decode base64 → Blob → URL.createObjectURL o singură dată per iconiță unique.
 * Cross-call cache: navigare repetate în același folder reuse-ează URL-urile fără re-decode.
 *
 * Eviction LRU la 500 entries — URL.revokeObjectURL apelat la evict pentru a evita memory leak Blob.
 *
 * Caller flow:
 *   1. wsClient.requestDirListing → response cu data.icons catalog
 *   2. iconCache.applyCatalog(icons) → decode + cache populate
 *   3. EntryIcon: iconCache.getBundle(hash)?.[urlBySize]
 */

import type { IconBundle as WsIconBundle } from './wsClient';

const LRU_CAPACITY = 500;

export interface IconBundle {
  readonly hash: string;
  readonly url20: string;
  readonly url48: string;
  readonly url96: string;
}

class IconCache {
  private readonly map = new Map<string, IconBundle>();

  /** Aplică catalog WS payload — decode toate hash-urile noi, refolosește hash-urile cunoscute. */
  applyCatalog(icons: Record<string, WsIconBundle> | undefined): void {
    if (!icons) return;
    for (const bundle of Object.values(icons)) {
      const hash = bundle?.hash;
      if (typeof hash !== 'string' || hash.length === 0) continue;
      if (this.map.has(hash)) {
        // Cache hit — touch LRU (re-insert pentru a-l muta la sfârșit).
        const existing = this.map.get(hash)!;
        this.map.delete(hash);
        this.map.set(hash, existing);
        continue;
      }
      const sizes = bundle.sizes;
      if (!sizes || typeof sizes !== 'object') continue;
      const url20 = base64ToBlobUrl(sizes['20']);
      const url48 = base64ToBlobUrl(sizes['48']);
      const url96 = base64ToBlobUrl(sizes['96']);
      if (!url48) continue;
      this.put(hash, { hash, url20: url20 ?? url48, url48, url96: url96 ?? url48 });
    }
  }

  getBundle(hash: string | null | undefined): IconBundle | null {
    if (!hash) return null;
    const bundle = this.map.get(hash);
    if (!bundle) return null;
    // Touch LRU.
    this.map.delete(hash);
    this.map.set(hash, bundle);
    return bundle;
  }

  /** Pentru un view mode, returnează URL pentru bundle existent. */
  static urlForViewMode(bundle: IconBundle, viewMode: 'list' | 'icons' | 'thumbnails' | 'large'): string {
    switch (viewMode) {
      case 'list': return bundle.url20;
      case 'thumbnails': return bundle.url96;
      case 'large': return bundle.url96;
      case 'icons': default: return bundle.url48;
    }
  }

  clearAll(): void {
    for (const bundle of this.map.values()) {
      revokeIfBlob(bundle.url20);
      revokeIfBlob(bundle.url48);
      revokeIfBlob(bundle.url96);
    }
    this.map.clear();
  }

  size(): number {
    return this.map.size;
  }

  private put(hash: string, bundle: IconBundle): void {
    this.map.set(hash, bundle);
    while (this.map.size > LRU_CAPACITY) {
      const first = this.map.keys().next();
      if (first.done) break;
      const evictHash = first.value;
      const evictBundle = this.map.get(evictHash);
      this.map.delete(evictHash);
      if (evictBundle) {
        revokeIfBlob(evictBundle.url20);
        revokeIfBlob(evictBundle.url48);
        revokeIfBlob(evictBundle.url96);
      }
    }
  }
}

function base64ToBlobUrl(b64: string | undefined): string | null {
  if (typeof b64 !== 'string' || b64.length === 0) return null;
  try {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'image/png' });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

function revokeIfBlob(url: string): void {
  if (url && url.startsWith('blob:')) {
    try { URL.revokeObjectURL(url); } catch { /* ignore */ }
  }
}

export const iconCache = new IconCache();
export { IconCache };
